import { useCallback } from "react";
import toast from "react-hot-toast";
import { ClipboardCopy } from "lucide-react";

import { cn } from "@/lib/utils";
import { copyToClipboard } from "@/utils/clipboard";
import { Button } from "@/components/ui/Button";

/**
 * A label, the id itself in monospace (truncated to first-8…last-4), and a
 * copy button.
 *
 * Half of the support tickets around ids ("which application is this?") get
 * answered when one person can hand another the exact id. The copy button uses
 * `copyToClipboard`, which falls back to `execCommand` when
 * `navigator.clipboard` is undefined — every LAN install served over plain
 * http.
 *
 * `hideLabel` lets a caller render the label itself (e.g. a dropdown row with
 * the label on the left and a right-aligned copy button) while keeping the
 * labelled copy toasts and accessible names.
 *
 * `hideButton` drops the copy affordance entirely, for a read-only mention of
 * an id (e.g. "Applying for React Developer (8912bf15…c3f2)") that shares the
 * truncation and styling but isn't meant as a click target.
 *
 * @param {string} label  Human name of the id, e.g. "Candidate".
 * @param {string} value  The id itself. Renders nothing when absent.
 * @param {boolean} hideLabel  Suppress the label text, for a caller that renders its own (default: false).
 * @param {boolean} hideButton  Suppress the copy button, for a read-only mention (default: false).
 */
const shortenId = (value) => {
  const s = String(value ?? "").trim();
  if (s.length <= 13) return s;
  return `${s.slice(0, 8)}…${s.slice(-4)}`;
};

const CopyId = ({ label = "Record", value, className = "", idClassName = "", hideLabel = false, hideButton = false }) => {
  const handleCopy = useCallback(
    async (e) => {
      // CopyId often sits inside a clickable row (a table row, a card) —
      // without this the copy click also fires the row's own onClick/navigate.
      e.stopPropagation();
      const ok = await copyToClipboard(String(value ?? ""));
      if (ok) {
        toast.success(`${label} ID copied`);
      } else {
        toast.error(`Could not copy ${label} ID — copy it manually`);
      }
    },
    [label, value],
  );

  if (value == null || value === "") return null;

  const lowerLabel = label.toLowerCase();

  return (
    <span className={cn("inline-flex items-center gap-1.5 align-middle", className)}>
      {label && !hideLabel && <span className="text-[10px] font-medium uppercase tracking-wide text-text-muted">{label}</span>}

      <code className={cn("font-mono text-[11px] text-text-primary", idClassName)} title={String(value)}>
        {shortenId(value)}
      </code>

      {!hideButton && (
        <Button
          onClick={handleCopy}
          aria-label={`Copy ${lowerLabel} id`}
          title={`Copy ${lowerLabel} id`}
          className="rounded p-0.5 text-text-muted transition-colors hover:bg-background-icon/40 hover:text-text-primary"
        >
          <ClipboardCopy size={12} aria-hidden="true" />
        </Button>
      )}
    </span>
  );
};

export default CopyId;
